import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import FormCardShell from './FormCardShell';
import FormField from './FormField';
import { devError } from '../../lib/devLog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_CONFIG = (form, update) => [
  { id: 'buyer-name', label: 'Full Name *', required: true, key: 'fullName', placeholder: 'Jane Smith' },
  { id: 'buyer-company', label: 'Company Name (Optional)', key: 'companyName', placeholder: 'ABC Investments LLC' },
  { id: 'buyer-email', label: 'Email *', type: 'email', required: true, key: 'email', placeholder: 'jane@company.com' },
  { id: 'buyer-phone', label: 'Phone', type: 'tel', key: 'phone', placeholder: '(555) 987-6543' },
  { id: 'buyer-markets', label: 'Preferred Market(s)', key: 'preferredMarkets', placeholder: 'Texas, Florida, Arizona' },
  { id: 'buyer-buybox', label: 'Your Buy Box', type: 'textarea', key: 'buyBox', placeholder: 'Property Type, Price Range, Beds/Baths, Flip/Rental, Cash/Finance preferences...' },
].map((f) => ({ ...f, value: form[f.key], onChange: update(f.key), tint: 'amber' }));

const useBuyerSubmit = ({ source, sourceUrl }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (form) => {
    setLoading(true);
    try {
      await axios.post(`${API}/buyer-leads`, { ...form, source, sourceUrl });
      setSubmitted(true);
      toast.success("You've been added to our buyer list! Expect off-market deals soon.");
    } catch (err) {
      devError('buyer form submit failed:', err.message);
      toast.error('Failed to submit your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return { submitted, loading, submit };
};

const BuyerForm = ({ defaultMarket = '', source = 'home' }) => {
  const [form, setForm] = useState({
    fullName: '', companyName: '', email: '', phone: '',
    preferredMarkets: defaultMarket, buyBox: '',
  });
  const sourceUrl = typeof window !== 'undefined' ? window.location.href : '';
  const { submitted, loading, submit } = useBuyerSubmit({ source, sourceUrl });
  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const fields = FIELD_CONFIG(form, update);

  const handleSubmit = (e) => {
    e.preventDefault();
    submit(form);
  };

  return (
    <FormCardShell
      accent="amber"
      icon={Users}
      title="Join Our Buyer List"
      description="Get exclusive access to off-market deals tailored to your investment criteria."
      submitted={submitted}
      thankYouTitle="Welcome Aboard!"
      thankYouBody="Your buy box details have been received. We'll notify you of relevant off-market opportunities within 24 hours."
      contactCta="Questions?"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {fields.map((f) => <FormField key={f.id} {...f} />)}
        <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-6 transition-all duration-200 hover:shadow-lg">
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
          ) : (
            <><Users className="w-4 h-4 mr-2" />Join Buyer List</>
          )}
        </Button>
      </form>
    </FormCardShell>
  );
};

export default BuyerForm;
